import { reactive } from "vue"
import { AUTH_TOKEN_KEY, useOpsApi } from "src/api/client"
import { ApiError } from "src/generated/client"
import type { IncidentGroupOverview, MentionNotifyDto, MeResponse } from "src/generated/models"
import type { Permission } from "src/generated/permissions"
import { AUTH_TOKEN_COOKIE, IS_MOCK, OPS_API_BASE_PATH, OPS_SYSTEM_HOST } from "src/app/opsSystem"
import { appRouter, PARAM_RETURN_PATH_FULL } from "src/router/appRouter"
import { useIncidentStore } from "./incident"
import { useOnCallStore } from "./oncall"

/**
 * The session plus the server-resolved permissions. Permission checks go through [has] against the
 * granted-key set (`MeResponse.grantedPermissions`), so the frontend never needs the group/permission
 * rules of its own.
 */
export interface AuthStore {
  /** `null` means not checked yet. */
  authenticated: boolean | null
  /** true while the session is expired and the lock overlay is shown. */
  lockedOut: boolean
  /** bearer token for ops api requests (also kept in localStorage; the transport reads it from there). */
  token: string | null
  userId: string | null
  firstName: string | null
  fullName: string | null
  /**
   * The user's own phone number (E.164) — the paging target of the phone channel, editable in the
   * profile. `null` means none is on file and a phone escalation does not reach this user.
   */
  phone: string | null
  /** How the user hears about mentions; `null` until the first `me()`. */
  mentionNotify: MentionNotifyDto | null
  incidentGroups: IncidentGroupOverview[]
  grantedPermissions: Set<string>
  /**
   * Whether this system has a static-sites host configured (`MeResponse.staticSitesEnabled`). Not a
   * permission: the user may hold all of them while the *system* has nowhere to deploy to, so the
   * section stays visible and read-only rather than disappearing.
   */
  staticSitesEnabled: boolean
  cmsVersion: string | null
  backendVersion: string | null
  /** Version of the frontend bundle currently running — frozen at the first `me()` (see [checkForNewVersion]). */
  frontendVersion: string | null
  /** A newer frontend has been deployed since this bundle loaded — show the reload banner + auto-reload. */
  newVersionAvailable: boolean

  /** Checks an effective permission by its key (e.g. `"DevicePermission.Read"`). */
  has(key: Permission): boolean
  /** Bounce to the IdP (localhost can't do OIDC, so it goes to the LIVE/DEV host). */
  redirectToLogin(): void
  /** Restore a saved token from localStorage and validate it. Returns true if authenticated. */
  loadLogin(): Promise<boolean>
  /** Persist the token and load `me()` (then on-call + incident counts). */
  saveLogin(newToken: string): Promise<void>
  clearLogin(): void
  logoutToLogin(): void
  /** Detected session expiry: clear state and show the lock overlay. */
  lockOut(): void
  /** Try to restore the session (e.g. after another tab logged in). Returns true on success. */
  tryRestoreSession(): Promise<boolean>
  /** Re-fetch `me()` and update the session (e.g. after the caller's own roles/permissions changed). */
  refresh(): Promise<void>
  updateInfo(info: MeResponse): void
  /**
   * Compare the deployed frontend version reported by a fresh `me()` against the running bundle and
   * raise [newVersionAvailable] when it has changed. Called both from [updateInfo] (login/restore)
   * and from the background `me()` poll. Never moves [frontendVersion] — that stays frozen at the
   * loaded version so the comparison keeps detecting the delta and the version tooltip stays honest.
   */
  checkForNewVersion(info: MeResponse): void
}

const store: AuthStore = reactive<AuthStore>({
  authenticated: null,
  lockedOut: false,
  token: localStorage.getItem(AUTH_TOKEN_KEY),
  userId: null,
  firstName: null,
  fullName: null,
  phone: null,
  mentionNotify: null,
  incidentGroups: [] as IncidentGroupOverview[],
  grantedPermissions: new Set<string>(),
  staticSitesEnabled: false,
  cmsVersion: null,
  backendVersion: null,
  frontendVersion: null,
  newVersionAvailable: false,

  has(key: Permission): boolean {
    return this.grantedPermissions.has(key)
  },

  redirectToLogin(): void {
    if (IS_MOCK) {
      // No IdP redirect in mock mode: sign in immediately, then honour the returnPath on the Login
      // route (mirrors the auth guard's authenticated-on-Login redirect) — falling back to the start
      // page when there is none (e.g. the session-lock overlay re-login).
      void this.saveLogin("mock-token").then(() => {
        const router = appRouter()
        const raw = router.currentRoute.value.query[PARAM_RETURN_PATH_FULL]
        const returnPath = (Array.isArray(raw) ? raw[0] : raw) ?? null
        void router.push(returnPath ? returnPath : { name: "Start" })
      })
      return
    }
    // localhost can't perform OIDC, so bounce to the real LIVE/DEV host
    const returnUrl = encodeURIComponent(window.location.href)
    window.location.href = `https://${OPS_SYSTEM_HOST}${OPS_API_BASE_PATH}/auth/login?return=${returnUrl}`
  },

  updateInfo(info: MeResponse): void {
    this.userId = info.userId
    this.firstName = info.firstName
    this.fullName = info.fullName
    this.phone = info.phone ?? null
    this.mentionNotify = info.mentionNotify
    this.incidentGroups = [...info.incidentGroups]
    this.grantedPermissions = new Set(info.grantedPermissions)
    this.staticSitesEnabled = info.staticSitesEnabled
    this.cmsVersion = info.versions.cms
    this.backendVersion = info.versions.backend
    // Detect first (against the frozen baseline), then seed the baseline on the very first call.
    this.checkForNewVersion(info)
    if (this.frontendVersion === null) this.frontendVersion = info.versions.frontend
  },

  checkForNewVersion(info: MeResponse): void {
    // A changed deployed frontend version means the running bundle is stale → prompt a reload.
    // (A backend change that breaks compatibility is contracted to bump the frontend version too.)
    if (this.frontendVersion !== null && info.versions.frontend !== this.frontendVersion) {
      this.newVersionAvailable = true
    }
  },

  async loadLogin(): Promise<boolean> {
    const savedToken = localStorage.getItem(AUTH_TOKEN_KEY)
    if (savedToken) await this.saveLogin(savedToken)
    return this.authenticated === true
  },

  async saveLogin(newToken: string): Promise<void> {
    this.token = newToken
    localStorage.setItem(AUTH_TOKEN_KEY, newToken)
    document.cookie = `${AUTH_TOKEN_COOKIE}=${newToken}; path=/; SameSite=Strict; Secure`

    try {
      this.updateInfo(await useOpsApi().auth.me())
      this.authenticated = true
      this.lockedOut = false
    } catch (e) {
      // only clear on an auth error, not on a transient server/network failure
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        this.clearLogin()
      }
    }

    if (this.authenticated === true) {
      try {
        await useOnCallStore().updateCurrent()
        await useIncidentStore().updateCounts()
      } catch {
        // ignore
      }
    }
  },

  clearLogin(): void {
    this.authenticated = false
    this.token = null
    localStorage.removeItem(AUTH_TOKEN_KEY)
    document.cookie = `${AUTH_TOKEN_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict; Secure`
    this.userId = null
    this.firstName = ""
    this.fullName = ""
    this.phone = null
    this.mentionNotify = null
    this.incidentGroups = []
    this.grantedPermissions = new Set<string>()
    this.staticSitesEnabled = false
  },

  logoutToLogin(): void {
    this.clearLogin()
    void appRouter().push({ name: "Login" }) // no returnUrl — it's a clean logout
  },

  lockOut(): void {
    // The lock overlay is only for an *active* session that expired mid-use — it traps the user in
    // place so they don't lose where they were. A 401 raised while validating a saved token on a
    // cold start (e.g. a stale token from the previous day) must NOT show the overlay: there
    // `authenticated` is still null/false, so we just clear the dead token and let the auth guard
    // route to the Login page.
    if (this.authenticated !== true) {
      this.clearLogin()
      return
    }
    this.clearLogin()
    useOnCallStore().clearCurrent()
    useIncidentStore().clearCounts()
    this.lockedOut = true
  },

  async tryRestoreSession(): Promise<boolean> {
    if (this.authenticated !== true) await this.loadLogin()
    if (this.authenticated === true) this.lockedOut = false
    return this.authenticated === true
  },

  async refresh(): Promise<void> {
    if (this.authenticated !== true) return
    try {
      this.updateInfo(await useOpsApi().auth.me())
    } catch (e) {
      // best-effort: a transient failure just leaves the previous grants in place until the next me()
      console.error(e)
    }
  },
})

export function useAuthStore(): AuthStore {
  return store
}
